import { useEffect, type ReactNode } from 'react';
import { AlertTriangle, Compass, Loader2, Wrench } from 'lucide-react';
import {
  useTenantByHost,
  type TenantHostStatus,
} from '@/features/tenant-config/hooks/useTenantByHost';
import { canonicalRootDomain } from '@/lib/tenant/tenant-host';
import { describeBootFailure } from './boot-failure-copy';
import { useTenantLogo } from '@/hooks/useTenantLogo';
import { applyTenantColors } from '@/lib/theme/tenant-colors';
import { applyTenantHead } from '@/lib/theme/tenant-head';
import { applyTenantFont } from '@/lib/theme/tenant-font';

const ROOT_DOMAIN = canonicalRootDomain();

type BlockedStatus = Exclude<TenantHostStatus, 'ACTIVE' | 'PROVISIONING'>;

const BLOCKED_COPY: Record<
  BlockedStatus,
  { heading: string; body: string; tone: 'amber' | 'rose' }
> = {
  MAINTENANCE: {
    heading: 'Undergoing maintenance',
    body: 'This workspace is temporarily offline for scheduled maintenance. It will be back shortly — no action is needed on your side.',
    tone: 'amber',
  },
  SUSPENDED: {
    heading: 'Workspace suspended',
    body: 'This workspace has been suspended by a platform administrator. Please contact your administrator to restore access.',
    tone: 'rose',
  },
  DEPROVISIONED: {
    heading: 'Workspace closed',
    body: 'This workspace is no longer available. If you believe this is a mistake, contact your platform administrator.',
    tone: 'rose',
  },
};

function BootScreen({
  icon,
  heading,
  body,
  tone = 'slate',
  detail,
}: {
  icon: ReactNode;
  heading: string;
  body: string;
  tone?: 'amber' | 'rose' | 'slate';
  detail?: ReactNode;
}) {
  const accent =
    tone === 'amber'
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : tone === 'rose'
        ? 'bg-rose-50 text-rose-700 ring-rose-200'
        : 'bg-slate-100 text-slate-600 ring-slate-200';
  // This screen paints its own light background rather than following the theme.
  const logo = useTenantLogo('light');

  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 px-6 py-16">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-8 flex h-10 items-center justify-center overflow-hidden">
          <img
            src={logo.src}
            alt={logo.alt}
            onError={logo.onError}
            className={
              logo.isCustom
                ? 'h-10 w-auto max-w-[12rem] object-contain'
                : 'h-36 w-auto object-contain'
            }
          />
        </div>
        <div
          className={`mx-auto grid size-14 place-items-center rounded-2xl ring-1 ${accent}`}
        >
          {icon}
        </div>
        <h1 className="mt-6 text-xl font-semibold tracking-tight text-slate-900">
          {heading}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{body}</p>
        {detail ? <div className="mt-6">{detail}</div> : null}
      </div>
    </main>
  );
}

export function TenantBootGate({ children }: { children: ReactNode }) {
  const {
    data,
    error,
    isPending,
    isError,
    isTenantHost,
    subdomain,
    isUnconfirmed,
  } = useTenantByHost();


  const primary = data?.branding?.['branding.primaryColor'];
  const accent = data?.branding?.['branding.accentColor'];
  useEffect(() => {
    if (primary && accent) applyTenantColors({ primary, accent });
  }, [primary, accent]);

  const tabTitle = data?.branding?.['branding.browserTabTitle'];
  const favicon = data?.branding?.['branding.faviconUrl'];
  const appName = data?.branding?.['branding.productName'];
  // The tab title falls back to the product name, matching the registry's own
  // derive; each field is applied independently so a workspace that set only
  // one of them still gets it.
  const headTitle = tabTitle?.trim() ? tabTitle : (appName ?? '');
  useEffect(() => {
    if (!headTitle && !favicon) return;
    applyTenantHead({
      title: headTitle,
      favicon: favicon ?? '',
      ...(appName ? { appName } : {}),
    });
  }, [headTitle, favicon, appName]);

  const fontFamily = data?.branding?.['branding.fontFamily'];
  useEffect(() => {
    if (fontFamily) applyTenantFont(fontFamily);
  }, [fontFamily]);

  // Platform origin (apex, localhost, an IP): no tenant to resolve. Super admins
  // work here, so the app must render normally.
  if (!isTenantHost) return <>{children}</>;


  if (isPending) {
    return (
      <BootScreen
        icon={<Loader2 className="size-6 animate-spin" aria-hidden="true" />}
        heading="Opening your workspace"
        body="One moment while we load your organization's settings."
      />
    );
  }

  
  if (isError && !data) {
    const status = (error as { response?: { status?: number } } | null)?.response
      ?.status;

    if (status !== 404) {
      const failure = describeBootFailure(status);

      return (
        <BootScreen
          icon={<AlertTriangle className="size-6" aria-hidden="true" />}
          heading={failure.heading}
          tone={failure.tone}
          body={failure.body}
          detail={
            <div className="space-y-1">
              {subdomain ? (
                <p className="font-mono text-xs text-slate-500">
                  {subdomain}.{ROOT_DOMAIN}
                </p>
              ) : null}
              <p className="font-mono text-xs text-slate-400">
                {failure.technical}
              </p>
            </div>
          }
        />
      );
    }

    return (
      // Deliberately the platform's own name: this screen renders precisely
      // because no tenant resolved, so there is no tenant branding to use.
      <BootScreen
        icon={<Compass className="size-6" aria-hidden="true" />}
        heading="Workspace not found"
        body={`There is no MineTech workspace at this address. Check the spelling of the subdomain, or ask your administrator for your organization's correct URL.`}
        detail={
          <div className="space-y-5">
            {subdomain ? (
              <p className="font-mono text-xs text-slate-500">
                {subdomain}.{ROOT_DOMAIN}
              </p>
            ) : null}
            <p className="text-xs text-slate-400">
              Your workspace address looks like{' '}
              <span className="font-mono text-slate-500">
                your-organization.{ROOT_DOMAIN}
              </span>
            </p>
          </div>
        }
      />
    );
  }

  // Never hard-block on a cached guess: an ACTIVE workspace once rendered
  // "Workspace suspended" from a status cached before it was reactivated.
  if (isUnconfirmed && data.status !== 'ACTIVE') {
    return (
      <BootScreen
        icon={<Loader2 className="size-6 animate-spin" aria-hidden="true" />}
        heading="Opening your workspace"
        body="One moment while we confirm your organization's settings."
      />
    );
  }

  // PROVISIONING is allowed through, or the IT administrator is locked out of
  // the onboarding they were invited to complete. Matches the backend.
  if (data.status !== 'ACTIVE' && data.status !== 'PROVISIONING') {
    const copy = BLOCKED_COPY[data.status as BlockedStatus] as
      | (typeof BLOCKED_COPY)[BlockedStatus]
      | undefined;

    // A status the backend added that this build has no copy for.
    if (!copy) {
      return (
        <BootScreen
          icon={<AlertTriangle className="size-6" aria-hidden="true" />}
          heading="Workspace unavailable"
          body="This workspace is not currently available. Please contact your administrator."
          tone="rose"
          detail={
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              {data.name}
            </p>
          }
        />
      );
    }

    return (
      <BootScreen
        icon={
          data.status === 'MAINTENANCE' ? (
            <Wrench className="size-6" aria-hidden="true" />
          ) : (
            <AlertTriangle className="size-6" aria-hidden="true" />
          )
        }
        heading={copy.heading}
        body={copy.body}
        tone={copy.tone}
        detail={
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            {data.name}
          </p>
        }
      />
    );
  }

  return <>{children}</>;
}
